<template>
  <div class="shop-page">
    <van-nav-bar :title="shop.shop_name || '店铺'" fixed left-text="返回" left-arrow @click-left="goBack" />

    <div class="shop-body">
      <van-loading v-if="loading" class="page-loading" color="#ff6b35" />

      <template v-else>
        <!-- 店铺信息卡片：点击店铺头像可查看商家资质（需安全验证） -->
        <div class="shop-card">
          <div class="shop-logo" @click="onQualificationClick">
            <img v-if="shop.shop_logo" :src="shop.shop_logo" alt="店铺头像">
            <span v-else>🏪</span>
          </div>
          <div class="shop-info">
            <div class="shop-name">
              {{ shop.shop_name }}
              <span v-if="shop.is_official === 1" class="official-tag">自营</span>
              <span class="type-tag">{{ Number(shop.merchant_type) === 1 ? '个人商家' : '企业商家' }}</span>
            </div>
            <p class="shop-desc">{{ shop.shop_desc || '该店铺暂无简介' }}</p>
            <p class="shop-meta">在售商品 {{ shop.goods_count }} 件</p>
          </div>
        </div>

        <!-- 商家资质入口：需安全验证通过后查看 -->
        <div class="qual-entry" @click="onQualificationClick">
          <div class="entry-left">
            <i class="fas fa-shield-alt"></i>
            <span class="entry-title">商家资质</span>
            <span class="entry-tip">{{ qualificationTip }}</span>
          </div>
          <span class="entry-right">查看 ›</span>
        </div>

        <!-- 店铺商品 -->
        <div class="goods-title">全部商品</div>
        <div class="goods-list">
          <GoodsItem v-for="item in goodsList" :key="item.goods_id" :item="item" />
        </div>
        <div v-if="!goodsLoading && !goodsList.length" class="empty">
          <van-icon name="shop-o" size="48" color="#ccc" />
          <p>该店铺暂无在售商品</p>
        </div>
        <div v-if="hasMore" class="load-more" @click="loadGoods(goodsPage + 1)">
          {{ goodsLoading ? '加载中...' : '加载更多' }}
        </div>
        <div v-else-if="goodsList.length" class="load-more disabled">没有更多了</div>

      <div class="bottom-placeholder"></div>
      </template>
    </div>

    <!-- ============ 安全验证弹窗（查看商家资质前必须通过） ============ -->
    <van-popup
      v-model="verifyVisible"
      round
      position="center"
      :close-on-click-overlay="false"
      class="verify-popup"
    >
      <div class="popup-title">安全验证</div>
      <p class="popup-tip">为保护商家隐私，查看「商家资质」需输入登录密码完成安全验证</p>

      <div class="verify-form">
        <div class="form-row">
          <input
            v-model.trim="password"
            class="inp"
            type="password"
            maxlength="32"
            placeholder="请输入登录密码"
            @keyup.enter="submitVerify"
          >
        </div>

        <div class="form-row">
          <input
            v-model.trim="captchaCode"
            class="inp"
            maxlength="4"
            placeholder="图形验证码"
            @keyup.enter="submitVerify"
          >
          <img v-if="captchaImg" class="captcha-img" :src="captchaImg" alt="图形验证码" @click="loadCaptcha">
          <span v-else class="captcha-img placeholder" @click="loadCaptcha">点击获取</span>
        </div>

        <p class="verify-tip">验证通过后 5 分钟内可免重复验证（不会发送短信）</p>
      </div>

      <div class="popup-actions">
        <button type="button" class="btn cancel" @click="verifyVisible = false">取消</button>
        <button type="button" class="btn confirm" :disabled="verifying" @click="submitVerify">
          {{ verifying ? '验证中...' : '验证并查看' }}
        </button>
      </div>
    </van-popup>

    <!-- ============ 商家资质弹窗 ============ -->
    <van-popup v-model="qualVisible" round position="bottom" class="qual-popup">
      <div class="popup-title">
        商家资质
        <span class="close" @click="qualVisible = false">✕</span>
      </div>

      <div v-if="qualificationEmpty" class="qual-empty">该商家暂未上传资质信息</div>

      <template v-else>
        <div class="qual-row">
          <span class="label">{{ isPersonalQual ? '姓名' : '主体名称' }}</span>
          <span class="value">{{ qualification.subject_name || '-' }}</span>
        </div>
        <div class="qual-row" v-if="!isPersonalQual">
          <span class="label">统一社会信用代码</span>
          <span class="value">{{ qualification.credit_code || '-' }}</span>
        </div>
        <div class="qual-row" v-if="!isPersonalQual">
          <span class="label">法定代表人</span>
          <span class="value">{{ qualification.legal_name || '-' }}</span>
        </div>
        <div class="qual-row">
          <span class="label">{{ isPersonalQual ? '身份证号' : '证件号码' }}</span>
          <span class="value">{{ qualification.legal_id_no || '-' }}</span>
        </div>
        <div class="qual-row" v-if="qualification.other_desc">
          <span class="label">其他资质</span>
          <span class="value">{{ qualification.other_desc }}</span>
        </div>

        <div class="qual-images">
          <div
            v-for="img in qualificationImages"
            :key="img.url"
            class="qual-image"
            @click="previewQualImage(img.url)"
          >
            <img :src="img.url" :alt="img.label">
            <span>{{ img.label }}</span>
          </div>
        </div>

        <p v-if="qualification.updated_at" class="qual-time">更新于 {{ qualification.updated_at }}</p>
      </template>
    </van-popup>

    <!-- 资质图片放大查看 -->
    <ImageViewer v-model="previewVisible" :url="previewUrl" :thumb="previewUrl" />
  </div>
</template>

<script>
import {
  getShopDetailApi,
  getShopGoodsApi,
  verifyQualificationApi,
  getShopQualificationApi
} from '@/api/merchant'
import { getPicCodeApi } from '@/api/login'
import GoodsItem from '@/components/GoodsItem.vue'
import ImageViewer from '@/components/ImageViewer.vue'
import { Toast } from 'vant'

// 安全验证令牌有效期（秒）：与后端保持一致（5 分钟）
const TOKEN_EXPIRE_SECONDS = 300

export default {
  name: 'Shop',
  components: { GoodsItem, ImageViewer },
  data () {
    return {
      loading: false,
      merchantId: 0,
      shop: {},
      goodsList: [],
      goodsPage: 1,
      hasMore: false,
      goodsLoading: false,
      // ===== 安全验证（图形验证码 + 登录密码） =====
      verifyVisible: false,
      password: '',
      captchaImg: '',
      captchaKey: '',
      captchaCode: '',
      verifying: false,
      // 通过安全验证后拿到的短时令牌（有效期内无需重复验证）
      verifyToken: '',
      tokenExpireAt: 0,
      // ===== 商家资质 =====
      qualVisible: false,
      qualification: {},
      previewVisible: false,
      previewUrl: ''
    }
  },
  computed: {
    isLogin () {
      return !!this.$store.getters.token
    },
    qualificationEmpty () {
      const q = this.qualification || {}
      return !q.subject_name && !q.credit_code && !q.license_image
    },
    // 个人商家资质（实名认证）：展示「姓名 / 身份证」等标签
    isPersonalQual () {
      return Number(this.qualification.qual_type) === 1
    },
    // 资质入口提示文案（未通过审核时不在商城展示）
    qualificationTip () {
      if (Number(this.shop.has_qualification) === 1) return '需安全验证后查看'
      if (Number(this.shop.pending_qualification) === 1) return '资质审核中'
      return '商家暂未上传资质'
    },
    // 资质图片列表（仅展示已上传的）
    qualificationImages () {
      const q = this.qualification || {}
      const personal = Number(q.qual_type) === 1
      const list = []
      if (q.license_image) list.push({ label: '营业执照', url: q.license_image })
      if (q.legal_id_front) list.push({ label: personal ? '身份证（人像面）' : '法人证件（人像面）', url: q.legal_id_front })
      if (q.legal_id_back) list.push({ label: personal ? '身份证（国徽面）' : '法人证件（国徽面）', url: q.legal_id_back })
      if (q.other_image) list.push({ label: '其他资质', url: q.other_image })
      return list
    }
  },
  created () {
    this.merchantId = Number(this.$route.params.id) || 0
    this.loadShop()
  },
  methods: {
    goBack () {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.push('/home')
      }
    },
    // ==================== 店铺信息 / 商品 ====================
    async loadShop () {
      if (this.merchantId <= 0) {
        Toast('店铺不存在')
        return
      }
      this.loading = true
      try {
        const { data } = await getShopDetailApi(this.merchantId)
        this.shop = data || {}
      } catch (e) {
        // 错误提示由请求拦截器统一处理
      } finally {
        this.loading = false
      }
      this.loadGoods(1)
    },
    async loadGoods (page) {
      if (this.goodsLoading) return
      this.goodsLoading = true
      try {
        const { data } = await getShopGoodsApi(this.merchantId, page)
        const list = (data && data.list) || []
        this.goodsList = page === 1 ? list : this.goodsList.concat(list)
        this.goodsPage = (data && data.page) || page
        this.hasMore = this.goodsPage < ((data && data.totalPage) || 1)
      } catch (e) {
        // 静默失败
      } finally {
        this.goodsLoading = false
      }
    },
    // ==================== 商家资质：安全验证 ====================
    onQualificationClick () {
      if (!this.isLogin) {
        Toast('请先登录后再查看商家资质')
        this.$router.push(`/login?redirect=${encodeURIComponent(this.$route.fullPath)}`)
        return
      }
      if (!this.shop.has_qualification) {
        Toast(Number(this.shop.pending_qualification) === 1
          ? '商家资质正在审核中，暂不可查看'
          : '该商家暂未上传资质信息')
        return
      }
      // 安全验证有效期内：直接查看，无需重复验证
      if (this.verifyToken && Date.now() < this.tokenExpireAt) {
        this.fetchQualification()
        return
      }
      this.openVerify()
    },
    openVerify () {
      this.verifyVisible = true
      this.password = ''
      this.captchaCode = ''
      this.loadCaptcha()
    },
    // 图形验证码
    async loadCaptcha () {
      try {
        const { data } = await getPicCodeApi()
        this.captchaImg = (data && data.img) || ''
        this.captchaKey = (data && data.key) || ''
      } catch (e) {
        // 静默失败
      }
    },
    // 安全验证：图形验证码 + 登录密码 → 拿到短时令牌 → 查看资质
    async submitVerify () {
      if (!this.password) {
        Toast('请输入登录密码')
        return
      }
      if (!this.captchaCode) {
        Toast('请输入图形验证码')
        return
      }
      this.verifying = true
      try {
        const { data } = await verifyQualificationApi(this.password, this.captchaCode, this.captchaKey)
        this.verifyToken = (data && data.token) || ''
        const expireIn = Number((data && data.expire_in) || TOKEN_EXPIRE_SECONDS)
        this.tokenExpireAt = Date.now() + expireIn * 1000
        this.verifyVisible = false
        this.fetchQualification()
      } catch (e) {
        // 图形验证码为一次性使用：验证失败后刷新，避免重复提交同一个验证码
        this.captchaCode = ''
        this.loadCaptcha()
      } finally {
        this.verifying = false
      }
    },
    async fetchQualification () {
      try {
        const { data } = await getShopQualificationApi(this.merchantId, this.verifyToken)
        this.qualification = (data && data.qualification) || {}
        this.qualVisible = true
      } catch (e) {
        // 令牌失效：清空后要求重新做安全验证
        this.verifyToken = ''
        this.tokenExpireAt = 0
      }
    },
    previewQualImage (url) {
      this.previewUrl = url
      this.previewVisible = true
    }
  }
}
</script>

<style lang="less" scoped>
.shop-page {
  min-height: 100vh;
  background: #f5f6f8;

  .shop-body {
    padding: 60px 12px 0;
  }

  .page-loading {
    padding: 60px 0;
    text-align: center;
  }

  // 店铺信息卡片
  .shop-card {
    display: flex;
    background: #fff;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);

    .shop-logo {
      width: 62px;
      height: 62px;
      border-radius: 12px;
      overflow: hidden;
      background: #fff5f0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 30px;
      flex-shrink: 0;
      margin-right: 14px;
      cursor: pointer;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .shop-info {
      flex: 1;
      min-width: 0;

      .shop-name {
        font-size: 16px;
        font-weight: 600;
        color: #222;
        display: flex;
        align-items: center;

        .official-tag {
          margin-left: 6px;
          font-size: 10px;
          font-weight: 400;
          color: #ff6b35;
          border: 1px solid #ffd2b0;
          border-radius: 4px;
          padding: 0 4px;
          line-height: 14px;
        }

        // 商家类型标识（个人 / 企业）
        .type-tag {
          margin-left: 6px;
          font-size: 10px;
          font-weight: 400;
          color: #4b5563;
          border: 1px solid #e5e7eb;
          border-radius: 4px;
          padding: 0 4px;
          line-height: 14px;
        }
      }

      .shop-desc {
        margin: 6px 0 0;
        font-size: 12px;
        color: #999;
        line-height: 18px;
      }

      .shop-meta {
        margin: 6px 0 0;
        font-size: 12px;
        color: #bbb;
      }
    }
  }

  // 商家资质入口（需安全验证）
  .qual-entry {
    margin-top: 10px;
    background: #fff;
    border-radius: 12px;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;

    .entry-left {
      display: flex;
      align-items: center;
      min-width: 0;

      i {
        color: #ff6b35;
        font-size: 16px;
        margin-right: 8px;
      }

      .entry-title {
        font-size: 14px;
        color: #333;
        font-weight: 500;
      }

      .entry-tip {
        margin-left: 8px;
        font-size: 12px;
        color: #b0b0b0;
      }
    }

    .entry-right {
      font-size: 13px;
      color: #ff6b35;
      flex-shrink: 0;
    }
  }

  .goods-title {
    margin: 16px 4px 8px;
    font-size: 15px;
    font-weight: 600;
    color: #1a1a2e;
  }

  .goods-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .empty {
    padding: 50px 0;
    text-align: center;
    color: #999;
    font-size: 13px;
  }

  .load-more {
    margin-top: 12px;
    padding: 10px;
    text-align: center;
    font-size: 13px;
    color: #ff6b35;
    background: #fff;
    border-radius: 10px;
    cursor: pointer;

    &.disabled {
      color: #bbb;
      cursor: default;
    }
  }

  .bottom-placeholder {
    height: 24px;
  }
}

// ============ 安全验证弹窗 ============
.verify-popup {
  width: 86%;
  max-width: 340px;
  padding: 20px 18px;

  .popup-title {
    font-size: 16px;
    font-weight: 600;
    color: #222;
    text-align: center;
  }

  .popup-tip {
    margin: 8px 0 16px;
    font-size: 12px;
    color: #999;
    line-height: 18px;
    text-align: center;
  }

  .verify-form {
    .form-row {
      display: flex;
      align-items: center;
      margin-bottom: 12px;

      .inp {
        flex: 1;
        height: 40px;
        min-width: 0;
        border: 1px solid #eee;
        border-radius: 8px;
        padding: 0 12px;
        font-size: 14px;
        outline: none;

        &:focus {
          border-color: #ffb27a;
        }
      }

      .captcha-img {
        width: 100px;
        height: 40px;
        margin-left: 10px;
        border-radius: 6px;
        background: #f5f5f5;
        flex-shrink: 0;
        object-fit: cover;
        cursor: pointer;
      }

      .captcha-img.placeholder {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        color: #999;
      }
    }

    .verify-tip {
      margin: 0;
      font-size: 12px;
      color: #b0b0b0;
    }
  }

  .popup-actions {
    display: flex;
    gap: 10px;
    margin-top: 18px;

    .btn {
      flex: 1;
      height: 40px;
      border: none;
      border-radius: 20px;
      font-size: 14px;

      &.cancel {
        background: #f2f3f5;
        color: #666;
      }

      &.confirm {
        background: linear-gradient(90deg, #ecb53c, #ff9211);
        color: #fff;

        &:disabled {
          opacity: 0.6;
        }
      }
    }
  }
}

// ============ 商家资质弹窗 ============
.qual-popup {
  padding: 20px 18px 26px;
  max-height: 80vh;
  overflow-y: auto;

  .popup-title {
    position: relative;
    font-size: 16px;
    font-weight: 600;
    color: #222;
    text-align: center;
    margin-bottom: 14px;

    .close {
      position: absolute;
      right: 0;
      top: -2px;
      font-size: 14px;
      color: #bbb;
      cursor: pointer;
    }
  }

  .qual-empty {
    padding: 30px 0;
    text-align: center;
    color: #999;
    font-size: 13px;
  }

  .qual-row {
    display: flex;
    font-size: 13px;
    padding: 8px 0;
    border-bottom: 1px dashed #f0f0f0;

    .label {
      width: 122px;
      flex-shrink: 0;
      color: #999;
    }

    .value {
      flex: 1;
      color: #333;
      word-break: break-all;
    }
  }

  .qual-images {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 14px;

    .qual-image {
      width: 100px;
      text-align: center;
      cursor: pointer;

      img {
        width: 100px;
        height: 100px;
        object-fit: cover;
        border-radius: 8px;
        background: #f5f5f5;
      }

      span {
        display: block;
        margin-top: 4px;
        font-size: 11px;
        color: #999;
      }
    }
  }

  .qual-time {
    margin: 14px 0 0;
    font-size: 12px;
    color: #bbb;
    text-align: right;
  }
}
</style>
